import { designTokens } from '@cinch-labs/design-system'
import { getBounds, styles as mapStyles } from './utils'
import { GoogleMap, Marker } from '@react-google-maps/api'
import { useCallback, useEffect, useState } from 'react'
import { FindAStoreMapProps } from './types'
import { useViewport } from '@cinch-labs/shared-util'
import { StoreWithDistance } from '../../../hooks/types'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useGoogleMapsLoader } from '../../../hooks/use-google-maps-loader'

const {
  color: { vividViolet },
} = designTokens.customProperties.ds

export const StoreMap = ({
  stores,
  selectedStore,
  setSelectedStore,
  customerLocation,
  analyticsCategory = 'cinch_servicing',
}: FindAStoreMapProps) => {
  const { trackEvent } = useAnalytics()

  const { isLoaded } = useGoogleMapsLoader()
  const [map, setMap] = useState<google.maps.Map>()

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map)
  }, [])

  const { vw, vh } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })
  const markers = stores.map((s) => ({
    id: s.store.id,
    lat: s.store.coordinates.latitude,
    lng: s.store.coordinates.longitude,
    store: s,
  }))

  useEffect(() => {
    if (!map) {
      return
    }
    if (selectedStore) {
      map.setZoom(12)
      map.panTo({
        lat: selectedStore.coordinates.latitude,
        lng: selectedStore.coordinates.longitude,
      })
    } else {
      if (customerLocation) {
        const collectionSiteBounds = getBounds(markers)
        const ne = collectionSiteBounds.getNorthEast()
        const sw = collectionSiteBounds.getSouthWest()
        const latDelta = Math.max(
          Math.abs(customerLocation.latitude - ne.lat()),
          Math.abs(customerLocation.latitude - sw.lat()),
        )
        const lngDelta = Math.max(
          Math.abs(customerLocation.longitude - ne.lng()),
          Math.abs(customerLocation.longitude - sw.lng()),
        )
        const minLat = customerLocation.latitude - latDelta
        const maxLat = customerLocation.latitude + latDelta

        const minLng = customerLocation.longitude - lngDelta
        const maxLng = customerLocation.longitude + lngDelta
        const bounds = new google.maps.LatLngBounds(
          { lat: minLat, lng: minLng },
          { lat: maxLat, lng: maxLng },
        )
        map.fitBounds(bounds)
      }
      map.setZoom(8)
    }
  }, [map, vw, vh, selectedStore, customerLocation, stores])

  const onMarkerClick = (store: StoreWithDistance) => {
    const searchStoreEvent = {
      eventName: `Store Marker`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: analyticsCategory,
          action: 'store_marker_click',
          label: store.store.name,
        },
      },
    }
    trackEvent(searchStoreEvent)
    setSelectedStore(store)
  }

  const locationSvgPath =
    'M25.412 16.077c-1.185 3.283-4.63 9.502-6.634 11.864a3 3 0 0 1-4.782-.274c-2.822-4.224-4.677-7.416-6.169-10.87C5.303 10.95 8.945 4.46 14.551 3.21c.631-.14 1.27-.21 1.911-.21 4.288 0 8.127 3.117 9.223 7.371.497 1.93.406 3.823-.273 5.706zM22.78 11.12C22.02 8.17 19.358 6 16.462 6c-.416 0-.837.045-1.259.138-3.427.765-6.527 5.057-4.622 9.469 1.592 3.686 3.67 7.041 5.91 10.393 1.814-2.138 5.066-8.08 6.099-10.941.475-1.318.536-2.596.19-3.94zM16.5 15c-1.493 0-3.01-1.343-3.01-3s1.517-3 3.01-3c1.493 0 2.913 1.5 2.913 3s-1.42 3-2.913 3z'

  if (!isLoaded) {
    return null
  }

  return (
    <GoogleMap
      mapContainerStyle={{
        height: '100%',
        width: '100%',
      }}
      options={{
        mapTypeControl: false,
        clickableIcons: false,
        styles: mapStyles,
        streetViewControl: false,
        fullscreenControl: false,
      }}
      onLoad={onLoad}
      zoom={9}
    >
      {markers.map((marker) => (
        <Marker
          title={marker.store.store.name}
          icon={
            {
              size: { width: 7, height: 6 },
              anchor: { x: 19 - 7, y: 19 + 6 },
              fillColor: vividViolet,
              fillOpacity: 1,
              strokeColor: vividViolet,
              path: locationSvgPath,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          key={marker.id}
          position={marker}
          onClick={() => onMarkerClick(marker.store)}
        />
      ))}
      {customerLocation && (
        <Marker
          title={customerLocation.postcode}
          position={{
            lat: customerLocation?.latitude,
            lng: customerLocation.longitude,
          }}
        />
      )}
    </GoogleMap>
  )
}

export default StoreMap
